import { IconBell } from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotificationsContext } from "@/contexts/notifications.context";
import { cn } from "@/lib/utils";

export function NotificationsBell() {
  const { notifications, unreadCount, markRead, markAllRead } =
    useNotificationsContext();
  const navigate = useNavigate();

  async function handleClick(id: string, redirectPath: string, isRead: boolean) {
    if (!isRead) await markRead(id);
    if (redirectPath) navigate(redirectPath);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8">
          <IconBell className="size-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="max-h-[min(480px,60dvh)] w-[calc(100vw-2rem)] overflow-y-auto sm:w-80"
      >
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {unreadCount > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                markAllRead();
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Mark all read
            </button>
          )}
        </div>

        <DropdownMenuSeparator />

        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No notifications
          </div>
        ) : (
          notifications.map((n) => (
            <DropdownMenuItem
              key={n.notification_id}
              className={cn(
                "flex flex-col items-start gap-1 px-3 py-2.5 cursor-pointer",
                !n.is_read && "bg-muted/50",
              )}
              onClick={() =>
                handleClick(n.notification_id, n.redirect_path, n.is_read)
              }
            >
              <div className="flex w-full items-start gap-2">
                {n.icon_url ? (
                  <img
                    src={n.icon_url}
                    alt=""
                    className="mt-0.5 size-5 shrink-0 rounded-full object-cover"
                  />
                ) : !n.is_read ? (
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-blue-500" />
                ) : null}
                <p
                  className={cn(
                    "text-sm leading-snug flex-1",
                    !n.is_read && "font-medium",
                  )}
                >
                  {n.body}
                </p>
              </div>
              <div className="flex w-full items-center justify-between pl-3.5">
                <span className="text-xs font-mono text-muted-foreground">
                  {n.action_type}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(n.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
